/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getActivityById,
  getActivityTimeSlotPricing,
  createActivityTimeSlot,
  deleteActivityTimeSlot,
  getActivityCloseOutDays,
  createActivityCloseOutDays,
  deleteActivityCloseOutDays,
} from "../../activityService";
import ActivityCancellation from "../../AddActivityTabs/ActivityCancellation";
import TimePicker from "../../../../../common/TimePicker";
import Toster from "../../../../../common/Toster";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatTime(t) {
  const s = (t ?? "").toString();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  let hh = Number(m[1]);
  const mm = m[2];
  const ampm = hh >= 12 ? "PM" : "AM";
  hh = hh % 12;
  if (hh === 0) hh = 12;
  return `${hh}:${mm} ${ampm}`;
}

const getTodayDate = () => new Date().toISOString().split("T")[0];

const emptySlotErrors = () => ({ start_time: "", end_time: "" });

const parseSlotFieldErrors = (error) => {
  const apiErrors = error?.response?.data?.errors;
  if (!apiErrors) return null;
  return {
    start_time: apiErrors.start_time?.[0] || "",
    end_time: apiErrors.end_time?.[0] || "",
  };
};

const validateSlotTimes = (start, end) => {
  if (!start || !end) return emptySlotErrors();
  if (end <= start) {
    return { start_time: "", end_time: "End time must be after start time." };
  }
  return emptySlotErrors();
};

const SectionAvailability = ({
  activityId,
  registerSave,
  registerCancellationSave,
  cancellationPolicies,
  setCancellationPolicies,
  enableCancellation,
  setEnableCancellation,
}) => {
  const queryClient = useQueryClient();
  const [newSlotStart, setNewSlotStart] = useState("");
  const [newSlotEnd, setNewSlotEnd] = useState("");
  const [isCloseOutEnabled, setIsCloseOutEnabled] = useState(false);
  const [closeOutPeriods, setCloseOutPeriods] = useState([]);
  const [offDays, setOffDays] = useState([]);
  const [blackoutStart, setBlackoutStart] = useState("");
  const [blackoutEnd, setBlackoutEnd] = useState("");
  const [isAddingSlot, setIsAddingSlot] = useState(false);
  const [slotFieldErrors, setSlotFieldErrors] = useState(emptySlotErrors);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");

  const notify = (type, message) => {
    setToastType(type);
    setToastMessage(message);
    setShowToast(true);
  };

  const { data: activityResponse } = useQuery({
    queryKey: ["activity", activityId],
    queryFn: () => getActivityById(activityId),
    enabled: !!activityId,
  });

  const isTimeSlotBased = Number(activityResponse?.data?.time_slot_based) === 1;

  const { data: timeSlotPricingResponse, isLoading: slotsLoading } = useQuery({
    queryKey: ["getActivityTimeSlotPricing", activityId],
    queryFn: () => getActivityTimeSlotPricing(activityId),
    enabled: !!activityId && isTimeSlotBased,
  });

  const { data: closeOutResponse } = useQuery({
    queryKey: ["getActivityCloseOut", activityId],
    queryFn: () => getActivityCloseOutDays(activityId),
    enabled: !!activityId,
  });

  const timeSlots = Array.isArray(timeSlotPricingResponse?.data)
    ? timeSlotPricingResponse.data
    : [];

  useEffect(() => {
    if (closeOutResponse?.data?.length > 0) {
      setIsCloseOutEnabled(true);
      setCloseOutPeriods(
        closeOutResponse.data.map((co) => ({
          id: co.id,
          startDate: co.start_date?.split("T")[0] || "",
          endDate: co.end_date?.split("T")[0] || "",
        }))
      );
      const first = closeOutResponse.data[0];
      if (first?.applicable_days) {
        const days = [];
        const map = { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" };
        Object.entries(first.applicable_days).forEach(([k, v]) => {
          if (v) days.push(map[k]);
        });
        setOffDays(days);
      }
    }
  }, [closeOutResponse]);

  const handleDayToggle = (day) => {
    setOffDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleAddSlot = async () => {
    if (!newSlotStart || !newSlotEnd) return;

    const clientErrors = validateSlotTimes(newSlotStart, newSlotEnd);
    if (clientErrors.end_time) {
      setSlotFieldErrors(clientErrors);
      return;
    }

    setSlotFieldErrors(emptySlotErrors());
    setIsAddingSlot(true);
    try {
      await createActivityTimeSlot(activityId, {
        start_time: newSlotStart,
        end_time: newSlotEnd,
        status: 1,
      });
      setNewSlotStart("");
      setNewSlotEnd("");
      setSlotFieldErrors(emptySlotErrors());
      await queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
      notify("success", "Time slot added successfully");
    } catch (error) {
      const fieldErrors = parseSlotFieldErrors(error);
      if (fieldErrors) {
        setSlotFieldErrors(fieldErrors);
        return;
      }
      notify("error", error?.response?.data?.message || "Failed to add time slot");
    } finally {
      setIsAddingSlot(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await deleteActivityTimeSlot(activityId, slotId);
      await queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
      notify("success", "Time slot removed");
    } catch (error) {
      notify("error", error?.response?.data?.message || "Failed to remove time slot");
    }
  };

  const handleSave = async () => {
    try {
      if (closeOutResponse?.data?.length > 0) {
        await Promise.all(
          closeOutResponse.data.map((curr) => deleteActivityCloseOutDays(activityId, curr.id))
        );
      }

      const hasWeeklyOffDays = offDays.length > 0;
      const hasBlackoutRange =
        isCloseOutEnabled &&
        Boolean(blackoutStart || blackoutEnd || closeOutPeriods[0]?.startDate);

      if (hasWeeklyOffDays || hasBlackoutRange) {
        const days = {
          sun: offDays.includes("Sun"),
          mon: offDays.includes("Mon"),
          tue: offDays.includes("Tue"),
          wed: offDays.includes("Wed"),
          thu: offDays.includes("Thu"),
          fri: offDays.includes("Fri"),
          sat: offDays.includes("Sat"),
        };
        const startDate = blackoutStart || closeOutPeriods[0]?.startDate || getTodayDate();
        const endDate = blackoutEnd || closeOutPeriods[0]?.endDate || "2099-12-31";
        await createActivityCloseOutDays(activityId, {
          start_date: startDate,
          end_date: endDate,
          applicable_days: days,
        });
      }

      await queryClient.invalidateQueries(["getActivityCloseOut", activityId]);
      await queryClient.invalidateQueries(["getActivityTimeSlotPricing", activityId]);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to save availability";
      notify("error", msg);
      throw error;
    }
  };

  useEffect(() => {
    registerSave?.(() => handleSave());
  }, [
    registerSave,
    activityId,
    isCloseOutEnabled,
    offDays,
    closeOutPeriods,
    blackoutStart,
    blackoutEnd,
    closeOutResponse,
  ]);

  if (!activityId) {
    return <p className="text-sm text-gray-500">Save general information first.</p>;
  }

  return (
    <div className="space-y-6">
      {!isTimeSlotBased && (
        <p className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Enable <strong>Time Slots</strong> in General Information to manage time-slot schedules here.
          Weekly off days below apply to blackout rules.
        </p>
      )}

      {isTimeSlotBased && (
        <div className="space-y-4">
          <h4 className="text-sm font-bold text-gray-900">Time slots</h4>
          <div className="flex flex-wrap gap-2">
            {slotsLoading ? (
              <span className="text-sm text-gray-500">Loading slots...</span>
            ) : timeSlots.length === 0 ? (
              <span className="text-sm text-gray-500">No slots yet</span>
            ) : (
              timeSlots.map((s) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                >
                  {formatTime(s.start_time)} – {formatTime(s.end_time)}
                  <button
                    type="button"
                    onClick={() => handleDeleteSlot(s.id)}
                    className="text-red-500 hover:text-red-700"
                    aria-label="Remove slot"
                  >
                    ×
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="flex flex-wrap items-start gap-3">
            <div className="w-52">
              <label className="mb-1 block text-xs font-medium text-gray-600">Start</label>
              <TimePicker
                value={newSlotStart}
                onChange={(value) => {
                  setNewSlotStart(value);
                  setSlotFieldErrors(emptySlotErrors());
                }}
                placeholder="Select start time"
                error={slotFieldErrors.start_time}
              />
            </div>
            <div className="w-52">
              <label className="mb-1 block text-xs font-medium text-gray-600">End</label>
              <TimePicker
                value={newSlotEnd}
                onChange={(value) => {
                  setNewSlotEnd(value);
                  setSlotFieldErrors(emptySlotErrors());
                }}
                placeholder="Select end time"
                error={slotFieldErrors.end_time}
              />
            </div>
            <button
              type="button"
              onClick={handleAddSlot}
              disabled={!newSlotStart || !newSlotEnd || isAddingSlot}
              className="mt-6 rounded-lg bg-primary px-4 py-2 text-xs font-bold uppercase text-white disabled:opacity-50"
            >
              {isAddingSlot ? "Adding..." : "+ Add slot"}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h4 className="text-sm font-bold text-gray-900">Weekly off days</h4>
        <p className="text-xs text-gray-500">
          Tap days when the activity is closed each week. Unmarked days stay open.
        </p>
        <div className="flex flex-wrap gap-2">
          {WEEKDAYS.map((day) => {
            const isOffDay = offDays.includes(day);
            return (
              <button
                key={day}
                type="button"
                onClick={() => handleDayToggle(day)}
                className={`min-h-[44px] min-w-[44px] rounded-lg border px-3 text-xs font-semibold transition ${
                  isOffDay
                    ? "border-red-300 bg-red-100 text-red-800 ring-2 ring-red-200"
                    : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-gray-900">Blackout dates</h4>
          <button
            type="button"
            onClick={() => setIsCloseOutEnabled(!isCloseOutEnabled)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              isCloseOutEnabled ? "bg-primary" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                isCloseOutEnabled ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {isCloseOutEnabled && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">From</label>
              <input
                type="date"
                value={blackoutStart}
                onChange={(e) => setBlackoutStart(e.target.value)}
                min={getTodayDate()}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">To</label>
              <input
                type="date"
                value={blackoutEnd}
                onChange={(e) => setBlackoutEnd(e.target.value)}
                min={blackoutStart || getTodayDate()}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-gray-100 pt-6">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-bold text-gray-900">Cancellation policy</h4>
          <button
            type="button"
            onClick={() => setEnableCancellation(!enableCancellation)}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              enableCancellation ? "bg-primary" : "bg-gray-200"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                enableCancellation ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        {enableCancellation && (
          <div data-prevent-section-scroll="true">
            <ActivityCancellation
            activityId={activityId}
            policies={cancellationPolicies}
            setPolicies={setCancellationPolicies}
            registerSave={registerCancellationSave}
          />
          </div>
        )}
      </div>
      {showToast && (
        <Toster
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default SectionAvailability;
